import React, { useState, useMemo } from "react";
import { Stack, Group, Title, TextInput, Button } from "@mantine/core";
import { debounce } from "lodash";
import { IconSearch, IconPlus } from "@tabler/icons-react";
import { router } from "@inertiajs/react";
import Table from "../../../components/AdminDataTable";
import type { SortParams } from "../../../components/AdminDataTable";
import { columns } from "./columns";
import type { AdminArticlesIndexProps, AdminArticleCard } from "./types";

const AdminArticlesIndex: React.FC<AdminArticlesIndexProps> = ({
  articles,
  pagination,
  sort,
}) => {
  const [searchValue, setSearchValue] = useState("");

  const searchQuery = (value: string) =>
    value ? { title_cont: value } : undefined;

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        router.get(
          "/admin/articles",
          { q: searchQuery(value), page: 1, per_page: pagination.per_page },
          { preserveState: true, preserveScroll: true },
        );
      }, 300),
    [],
  );

  const handleSearch = (value: string) => {
    setSearchValue(value);
    debouncedSearch(value);
  };

  const onPageChange = (page: number) =>
    router.get("/admin/articles", {
      q: searchQuery(searchValue),
      page,
      per_page: pagination.per_page,
      sort: sort.key,
      order: sort.order,
    });

  const onSortChange = (s: SortParams) => {
    const base = {
      q: searchQuery(searchValue),
      page: 1,
      per_page: pagination.per_page,
    };
    if (!s.order) {
      router.get("/admin/articles", base);
    } else {
      router.get("/admin/articles", { ...base, sort: s.key, order: s.order });
    }
  };

  const onRowClick = ({ record }: { record: AdminArticleCard }) =>
    router.visit(`/admin/articles/${record.slug}/edit`);

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={2}>Articles</Title>
        <Group gap="sm">
          <TextInput
            placeholder="Rechercher un article…"
            leftSection={<IconSearch size={16} />}
            value={searchValue}
            onChange={(e) => handleSearch(e.currentTarget.value)}
            w={260}
          />
          <Button
            color="brand"
            leftSection={<IconPlus size={16} />}
            onClick={() => router.visit("/admin/articles/new")}
          >
            Nouvel article
          </Button>
        </Group>
      </Group>
      <Table
        columns={columns}
        data={articles}
        rowKey="slug"
        onRowClick={onRowClick}
        sortStatus={
          sort.order
            ? { columnAccessor: sort.key, direction: sort.order }
            : undefined
        }
        onSortStatusChange={(s) =>
          onSortChange({ key: s.columnAccessor as string, order: s.direction })
        }
        totalRecords={pagination.total_pages * pagination.per_page}
        recordsPerPage={pagination.per_page}
        page={pagination.current_page}
        onPageChange={onPageChange}
        emptyState={
          <Stack align="center" gap="md" py="xl">
            <span>Aucun article. Créez votre premier article.</span>
            <Button
              color="brand"
              leftSection={<IconPlus size={16} />}
              onClick={() => router.visit("/admin/articles/new")}
            >
              Nouvel article
            </Button>
          </Stack>
        }
      />
    </Stack>
  );
};

export default AdminArticlesIndex;
